"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsForm = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _material = require("@mui/material");
var _DmsFlexContainer = require("../DmsFlexContainer/DmsFlexContainer");
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
var _notifications = require("../../service/notifications/notifications");
var _DmsFetch = require("../DmsFetch/DmsFetch");
var _HttpStatus = require("../../constants/HttpStatus");
var _DmsFormSubmitButton = require("./DmsFormSubmitButton");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
/**
 * Default function to parse form data and transform it into a JSON object.
 *
 * @param {FormData} formData - The form data to be parsed.
 * @param {Object} fieldTypes - An object describing the types of fields.
 * @returns {Object} - The parsed JSON object.
 */
const parseFormDataDefault = (formData, fieldTypes) => {
  const jsonData = {};
  formData.forEach((value, key) => {
    const keys = key.split(".");
    const fieldType = getFieldType(keys, fieldTypes);
    const processedValue = applyFieldType(value, fieldType);
    setNestedValue(jsonData, keys, processedValue);
  });
  const formElements = document.querySelectorAll("form input, form select, form textarea");
  formElements.forEach(element => {
    if (element.type === "checkbox" || element.name && !(element.name in jsonData)) {
      jsonData[element.name] = element.value;
    }
  });
  removeDuplicateFields(jsonData);
  return jsonData;
};

/**
 * Retrieves the field type for a given set of keys from the fieldTypes object.
 *
 * @param {Array<string>} keys - The keys that represent the field path.
 * @param {Object} fieldTypes - The fieldTypes object.
 * @returns {string|null} - The field type or null if not found.
 */
const getFieldType = (keys, fieldTypes) => {
  return keys.reduce((type, key) => type && type[key], fieldTypes) || null;
};

/**
 * Applies the correct transformation to the value based on the field type.
 *
 * @param {string} value - The value to be transformed.
 * @param {string|null} fieldType - The field type.
 * @returns {number|boolean|string} - The transformed value.
 */
const applyFieldType = (value, fieldType) => {
  switch (fieldType) {
    case "int":
      return parseInt(value, 10);
    case "float":
      return parseFloat(value);
    case "boolean":
      return value === "true";
    default:
      return value;
  }
};

/**
 * Sets a nested value within an object based on a given path.
 *
 * @param {Object} obj - The object to set the value in.
 * @param {Array<string>} path - The path representing where to set the value.
 * @param {any} value - The value to be set.
 */
const setNestedValue = (obj, path, value) => {
  path.reduce((acc, key, index) => {
    if (index === path.length - 1) {
      acc[key] = value;
    } else {
      acc[key] = acc[key] || {};
    }
    return acc[key];
  }, obj);
};

/**
 * Removes duplicate fields from the object where keys include a dot.
 *
 * @param {Object} obj - The object to remove duplicates from.
 */
const removeDuplicateFields = obj => {
  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      removeDuplicateFields(obj[key]);
    }
  });
  Object.keys(obj).forEach(key => {
    if (key.includes(".")) {
      delete obj[key];
    }
  });
};

/**
 * A form component with customizable validation, submission, and reset functionality.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.url - The URL to submit the form data to.
 * @param {string} [props.method="POST"] - The HTTP method to use when submitting the form.
 * @param {Function} [props.onSubmit] - A custom submission handler function.
 * @param {Function} [props.parseFormData=parseFormDataDefault] - Function to parse form data before submission.
 * @param {Object} [props.fieldTypes] - An object specifying the types of form fields for parsing.
 * @param {React.ReactNode} props.children - The child components, typically form fields, to be rendered inside the form.
 * @param {string} [props.dialogTitle] - The title of the confirmation dialog.
 * @param {string} [props.dialogMessage] - The message displayed in the confirmation dialog.
 * @param {string} [props.saveLabel] - Label for the save/submit button.
 * @param {string} [props.confirmLabel] - Label for the confirm button in the dialog.
 * @param {string} [props.cancelLabel] - Label for the cancel button in the dialog.
 * @returns {JSX.Element} The rendered DmsForm component.
 */
const DmsForm = _ref => {
  let {
    url,
    method = "POST",
    onSubmit,
    parseFormData = parseFormDataDefault,
    fieldTypes,
    children,
    dialogTitle = _reactIntlUniversal.default.get("ConfirmSave").d("Confirm Save"),
    dialogMessage = _reactIntlUniversal.default.get("ConfirmSaveMessage").d("Are you sure you want to save the changes?"),
    confirmLabel = _reactIntlUniversal.default.get("Confirm").d("Confirm"),
    cancelLabel = _reactIntlUniversal.default.get("Cancel").d("Cancel")
  } = _ref;
  const [dialogOpen, setDialogOpen] = (0, _react.useState)(false);
  const [formData, setFormData] = (0, _react.useState)(null);

  /**
   * Handles form submission, parses the form data, and opens a confirmation dialog.
   *
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
   */
  const handleFormSubmit = (0, _react.useCallback)(event => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const parsedData = parseFormData(formData, fieldTypes);
    setFormData(parsedData);
    setDialogOpen(true);
  }, [parseFormData, fieldTypes]);

  /**
   * Closes the confirmation dialog.
   */
  const closeDialog = () => setDialogOpen(false);

  /**
   * Confirms the save action by either submitting the form data or calling the custom onSubmit function.
   * Displays a notification based on the success or failure of the operation.
   */
  const onConfirmSave = async () => {
    closeDialog();
    try {
      const response = onSubmit ? await onSubmit(formData) : await (0, _DmsFetch.fetchData)(url, method, true, formData);
      const isSuccess = (response === null || response === void 0 ? void 0 : response.status) === _HttpStatus.HTTP_STATUS.OK_200 || (response === null || response === void 0 ? void 0 : response.status) === _HttpStatus.HTTP_STATUS.CREATED_201;
      const successMessage = _reactIntlUniversal.default.get("Savedsuccessfully").d("Saved successfully!");
      const failureMessage = _reactIntlUniversal.default.get("Failedtosavethedata").d("Failed to save the data!");
      _notifications.Notifications.toast(isSuccess ? successMessage : failureMessage, isSuccess ? _notifications.Notifications.TYPE_SUCCESS : _notifications.Notifications.TYPE_ERROR);
    } catch (error) {
      console.error("Submission error:", error);
      _notifications.Notifications.toast(_reactIntlUniversal.default.get("Failedtosavethedata").d("Failed to save the data!"), _notifications.Notifications.TYPE_ERROR);
    }
  };
  const content = _react.default.Children.toArray(children).find(child => child.type === DmsForm.Body);
  const footerLeft = _react.default.Children.toArray(children).find(child => child.type === DmsForm.FooterLeft);
  const footerCenter = _react.default.Children.toArray(children).find(child => child.type === DmsForm.FooterCenter);
  const footerRight = _react.default.Children.toArray(children).find(child => child.type === DmsForm.FooterRight);
  return /*#__PURE__*/_react.default.createElement(_material.Box, {
    component: "form",
    onSubmit: handleFormSubmit,
    noValidate: true
  }, content, /*#__PURE__*/_react.default.createElement(_material.Box, {
    sx: {
      paddingTop: 2
    }
  }, /*#__PURE__*/_react.default.createElement(_DmsFlexContainer.DmsFlexContainer, null, /*#__PURE__*/_react.default.createElement(_DmsFlexContainer.DmsFlexContainer.Left, null, footerLeft), /*#__PURE__*/_react.default.createElement(_DmsFlexContainer.DmsFlexContainer.Center, null, footerCenter), /*#__PURE__*/_react.default.createElement(_DmsFlexContainer.DmsFlexContainer.Right, null, footerRight, /*#__PURE__*/_react.default.createElement(_DmsFormSubmitButton.DmsFormSubmitButton, null)))), /*#__PURE__*/_react.default.createElement(_material.Dialog, {
    open: dialogOpen,
    onClose: closeDialog
  }, /*#__PURE__*/_react.default.createElement(_material.DialogTitle, null, dialogTitle), /*#__PURE__*/_react.default.createElement(_material.DialogContent, null, dialogMessage), /*#__PURE__*/_react.default.createElement(_material.DialogActions, null, /*#__PURE__*/_react.default.createElement(_material.Button, {
    onClick: closeDialog
  }, cancelLabel), /*#__PURE__*/_react.default.createElement(_material.Button, {
    onClick: onConfirmSave,
    color: "primary"
  }, confirmLabel))));
};
exports.DmsForm = DmsForm;
DmsForm.Body = _ref2 => {
  let {
    children
  } = _ref2;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, children);
};
DmsForm.FooterLeft = _ref3 => {
  let {
    children
  } = _ref3;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, children);
};
DmsForm.FooterCenter = _ref4 => {
  let {
    children
  } = _ref4;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, children);
};
DmsForm.FooterRight = _ref5 => {
  let {
    children
  } = _ref5;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, children);
};
DmsForm.propTypes = {
  url: _propTypes.default.string,
  method: _propTypes.default.string,
  onSubmit: _propTypes.default.func,
  parseFormData: _propTypes.default.func,
  fieldTypes: _propTypes.default.object,
  children: _propTypes.default.node.isRequired,
  dialogTitle: _propTypes.default.string,
  dialogMessage: _propTypes.default.string,
  saveLabel: _propTypes.default.string,
  confirmLabel: _propTypes.default.string,
  cancelLabel: _propTypes.default.string
};